import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { classesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { Class as ClassModel } from '../types/api';

/**
 * DeleteClassModal — confirmation dialog for class deletion. Pre-checks
 * the in-use count client-side and disables the Delete button when boxes
 * still reference the class (server returns 409 class_in_use defensively
 * if a race makes the count stale).
 */
interface DeleteClassModalProps {
    open: boolean;
    isDarkMode?: boolean;
    cls: ClassModel | null;
    inUseCount: number;
    projectId: string;
    orgId?: string;
    onClose: () => void;
    onDeleted: (classIndex: number) => void;
}

export default function DeleteClassModal({
    open,
    isDarkMode = false,
    cls,
    inUseCount: initialInUseCount,
    projectId,
    orgId,
    onClose,
    onDeleted,
}: DeleteClassModalProps) {
    const { t: tCommon } = useTranslation('common');
    const { t: tCanvas } = useTranslation('canvas');
    const { t: tErrors } = useTranslation('errors');
    const [currentInUse, setCurrentInUse] = useState(initialInUseCount);
    const [submitting, setSubmitting] = useState(false);

    if (!cls) return null;

    async function handleConfirm() {
        if (!cls) return;
        if (currentInUse > 0) return;
        setSubmitting(true);
        try {
            await classesApi.remove(projectId, cls.class_index, orgId);
            toast.success(tCanvas('class_deleted', { defaultValue: 'Class removed' }));
            onDeleted(cls.class_index);
            onClose();
        } catch (err) {
            if (err instanceof APIClientError && err.code === 'class_in_use') {
                const n = Number((err.body as unknown as { count?: number }).count ?? currentInUse);
                setCurrentInUse(n);
            } else {
                toast.error(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={submitting ? () => {} : onClose}
            title={tCanvas('delete_class_title', { defaultValue: 'Delete class' })}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose} disabled={submitting}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => void handleConfirm()}
                        disabled={currentInUse > 0 || submitting}
                        title={currentInUse > 0 ? tCanvas('class_in_use_tooltip', {
                            defaultValue: 'Reassign or delete the {{count}} boxes that use this class first.',
                            count: currentInUse,
                        }) : undefined}
                    >
                        {tCommon('delete')}
                    </Button>
                </>
            }
        >
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                {tCanvas('delete_class_body', {
                    defaultValue: 'Delete class "{{name}}"?',
                    name: cls.name,
                })}
            </p>
            {currentInUse > 0 && (
                <p className={cn('mt-3 text-sm rounded-lg border p-3',
                    isDarkMode ? 'bg-amber-900/20 border-amber-800 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800',
                )}>
                    {tCanvas('class_in_use_warning', {
                        defaultValue: '{{count}} boxes still use this class. Reassign or delete those boxes first — then come back and delete the class.',
                        count: currentInUse,
                    })}
                </p>
            )}
        </Modal>
    );
}
